import { OnboardingDialogsContext, Page } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import UnsavedChanges from "features/Influencer/Templates/TemplatesTable/EditTemplate/components/UnsavedChanges";
import { isPage } from "utils/general";
import { trackEvent } from "utils/tracking";
import { useBulkSend } from "utils/useBulkSend";
import { useGamification } from "utils/useGamification";

import CopywriterOfferDialog from "./CopywriterOfferDialog";
import { Step1Basics, Step1Data } from "./TemplateSteps/Step1Basics";
import { Step2Angle } from "./TemplateSteps/Step2Angle";
import ReviewBentoAiTemplate, {
  ReviewBentoAiTemplateHandle,
} from "./TemplateSteps/components/ReviewBentoAiTemplate";
import {
  readTemplateDefaults,
  writeTemplateDefaults,
} from "./TemplateSteps/helpers";
import { Step2Data } from "./TemplateSteps/schema";
import CreateTemplateDialogWrapper from "./Wrapper";

interface Props {
  isDialog?: boolean;
}

const CreateTemplateDialog = ({ isDialog = true }: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const location = useLocation();
  const { page, setPage, setCreateTemplateOnboardingDialogOpen } = useContext(
    OnboardingDialogsContext,
  );
  const {
    fetchOutreachTemplates,
    outreachTemplates,
    setCreateTemplateDialogOpen,
  } = useContext(OutreachTemplatesContext);
  const { addTemplateTaskCompleted } = useGamification();
  const { fetchOrganizationBulkSendSettings, fetchNiches } = useBulkSend();
  const reviewTemplateRef = useRef<ReviewBentoAiTemplateHandle>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [openIsDirtyWarning, setOpenIsDirtyWarning] = useState<boolean>(false);
  const [openUneditedWarning, setOpenUneditedWarning] =
    useState<boolean>(false);
  const [openCopywriterOffer, setOpenCopywriterOffer] =
    useState<boolean>(false);
  const [alreadyShownCopywriterOffer, setAlreadyShownCopywriterOffer] =
    useState<boolean>(false);
  const [isStep2Loading, setIsStep2Loading] = useState<boolean>(false);

  const isFirstTemplate = !outreachTemplates || outreachTemplates.length === 0;

  const [editTemplate, setEditTemplate] = useState<Template>({
    name: "",
    contentType: TemplateContentType.OUTREACH,
    subject: "",
    body: "",
  });
  const [followUpTemplate, setFollowUpTemplate] = useState<Template>({
    name: "",
    contentType: TemplateContentType.FOLLOWUP,
    subject: "",
    body: "",
  });

  const [step1Data, setStep1Data] = useState<Step1Data>({
    name: "",
    type: null,
  });
  const [step2Data, setStep2Data] = useState<Step2Data>({
    method: "ai",
    aboutYouItem: "portfolioUrl",
    bestWorkItem: "mostEngagedPost",
    writingStyleSource: "gmail",
  });

  const handleStep2Change = (next: Step2Data) => {
    setStep2Data(next);
    writeTemplateDefaults(currentOrg?.id, {
      aboutYouItem: next.aboutYouItem,
      bestWorkItem: next.bestWorkItem,
      writingStyleSource: next.writingStyleSource,
    });
  };

  const templateBeingEdit =
    step1Data?.type === TemplateContentType.FOLLOWUP
      ? followUpTemplate.id
      : editTemplate.id;

  const handleNext = async () => {
    trackEvent("Create Template Dialog Form - Next Button Pressed", {
      page,
      route: location.pathname,
      "Is First Template": isFirstTemplate,
    });
    if (page === Page.angle) {
      setPage(Page.template);
      return;
    }

    if (page === Page.template) {
      await fetchOutreachTemplates();
      if (isPage(routes.onboardingVaSelfServe)) {
        await fetchOrganizationBulkSendSettings();
        await fetchNiches();
      }
      confirmedClose();
    }
  };

  const handleBack = async () => {
    trackEvent("Create Template Dialog Form - Back Button Pressed", {
      page,
      route: location.pathname,
      "Is First Template": isFirstTemplate,
    });

    if (page === Page.angle) {
      if (isFirstTemplate) {
        handleClose();
        return;
      }
      setPage(Page.pitchType);
      return;
    }

    handleClose();
  };

  const handleClose = () => {
    trackEvent("Create Template Dialog Form Closed", {
      page,
      route: location.pathname,
    });
    if (isDirty) {
      setOpenIsDirtyWarning(true);
      return;
    }
    if (page === Page.template && !isDirty && editTemplate?.isAiGenerated) {
      setOpenUneditedWarning(true);
      return;
    }
    confirmedClose();
  };

  const confirmedClose = () => {
    // Show copywriter offer when user exits during first template creation
    // Show on any page except complete, as long as it's their first template
    if (
      isFirstTemplate &&
      !alreadyShownCopywriterOffer &&
      !isDirty &&
      page === Page.angle &&
      !isStep2Loading
    ) {
      trackEvent("Copywriter Template Offer - Shown", {
        page,
        route: location.pathname,
      });
      setOpenCopywriterOffer(true);
      return;
    }
    finalClose();
  };

  const finalClose = () => {
    setPage(Page.pitchType);
    setCreateTemplateOnboardingDialogOpen(false);
    setAlreadyShownCopywriterOffer(false);
    setCreateTemplateDialogOpen(false);
  };

  useEffect(() => {
    const saved = readTemplateDefaults(currentOrg?.id);
    if (
      saved?.writingStyleSource ||
      saved?.bestWorkItem ||
      saved?.aboutYouItem
    ) {
      setStep2Data((prev: Step2Data) => ({
        ...prev,
        bestWorkItem: saved.bestWorkItem ?? prev.bestWorkItem,
        aboutYouItem: saved.aboutYouItem ?? prev.aboutYouItem,
        writingStyleSource: saved.writingStyleSource,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (isFirstTemplate && page === Page.pitchType) {
      setPage(Page.angle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstTemplate, page]);

  return (
    <CreateTemplateDialogWrapper
      handleClose={handleClose}
      isDialog={isDialog}
      dialogTitle={""}
    >
      {page === Page.pitchType && (
        <Step1Basics
          value={step1Data}
          onChange={setStep1Data}
          onContinue={() => setPage(Page.angle)}
        />
      )}

      {page === Page.angle && (
        <Step2Angle
          value={step2Data}
          onChange={handleStep2Change}
          templateName={step1Data.name}
          onBack={handleBack}
          setOutreachTemplate={setEditTemplate}
          setFollowUpTemplate={setFollowUpTemplate}
          onNext={handleNext}
          contentType={step1Data?.type}
          onLoadingChange={setIsStep2Loading}
        />
      )}

      {isFirstTemplate &&
        editTemplate &&
        followUpTemplate &&
        page === Page.template && (
          <ReviewBentoAiTemplate
            ref={reviewTemplateRef}
            editTemplate={editTemplate}
            setEditTemplate={setEditTemplate}
            handleNext={handleNext}
            setIsDirty={setIsDirty}
            followUpTemplate={followUpTemplate}
            setFollowUpTemplate={setFollowUpTemplate}
          />
        )}

      {(page === Page.fromScratch ||
        (page === Page.template && !isFirstTemplate && templateBeingEdit)) && (
        <EditTemplateDialog
          propsHandleClose={handleBack}
          onSuccess={handleNext}
          propsTemplateId={
            page === Page.fromScratch
              ? undefined
              : templateBeingEdit?.toString()
          }
        />
      )}

      <ConfirmDialog
        header="Save template without editing?"
        subheader="You haven't made any changes to the AI-generated template. Would you like to continue editing or save it as is?"
        open={openUneditedWarning}
        handleClose={() => setOpenUneditedWarning(false)}
        handleCancel={() => {
          setOpenUneditedWarning(false);
          trackEvent("Create Template Dialog Form - Continue Editing", {
            page,
            route: location.pathname,
          });
        }}
        cancelText="Continue Editing"
        confirmText="Save As Is"
        handleConfirm={() => {
          setOpenUneditedWarning(false);
          reviewTemplateRef.current?.save();
          trackEvent("Create Template Dialog Form - Save As Is", {
            page,
            route: location.pathname,
          });
          addTemplateTaskCompleted(TemplateContentType.OUTREACH);
          addTemplateTaskCompleted(TemplateContentType.FOLLOWUP);
          confirmedClose();
        }}
      />

      <UnsavedChanges
        isUserFirstTemplate={isFirstTemplate}
        confirmedClose={confirmedClose}
        setIsDirty={setIsDirty}
        setOpenIsDirtyWarning={setOpenIsDirtyWarning}
        openIsDirtyWarning={openIsDirtyWarning}
      />

      <CopywriterOfferDialog
        open={openCopywriterOffer}
        handleClose={() => setOpenCopywriterOffer(false)}
        setAlreadyShownCopywriterOffer={setAlreadyShownCopywriterOffer}
        onDecline={() => {
          setOpenCopywriterOffer(false);
          setAlreadyShownCopywriterOffer(true);
          if (page === Page.pitchType) {
            finalClose();
          }
        }}
      />
    </CreateTemplateDialogWrapper>
  );
};

export default CreateTemplateDialog;
